import {
    AutoValue,
    autoValueCreate,
    autoValueIsAutoValue,
    enumIsEnumValue,
    evaluateFunction,
    HideableValue,
    PartiallyRequired,
    uuidCreate,
    Values
} from '@wix/devzai-utils-common';
import type {BymoPageHasSearchProps, BymoPageProps} from './bymo-page-props';
import type {
    BymoTemplateSpeedDialSocialLinksAppearance
} from '../../components/bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial-common';
import {
    bymoPageGetInnerNavigatablePageById,
    DoppeDtoInnerPageDynamicPagesIds,
    DoppeDtoInnerPageKnownIds,
    DoppeDtoInnerPageVerticalListPagesIds
} from '../types/doppe-dto-inner-page';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {
    type BymoPagePropsContentSettingsMetadata,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeActionTypeContentSpecialSettingsMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";

// AutoValue marker for "use profile picture from Link in Bio"
export const DoppeMenuLogoProfilePictureAutoValue = autoValueCreate<'profilePicture'>('profilePicture');


/**
 * Navigation menu item types - discriminated union similar to DoppActionCtaButton
 */
export const BymoPageNavigationMenuSettingsMenuPanelDisplayType = {
    SidePanel: 'SidePanel',
    FullScreen: 'FullScreen',
    DrawerTop: 'DrawerTop',
    DrawerBottom: 'DrawerBottom',
} as const;

export type BymoPageNavigationMenuSettingsMenuPanelDisplayType = Values<typeof BymoPageNavigationMenuSettingsMenuPanelDisplayType>;

/**
 * Menu type for desktop navigation - determines how the menu is displayed
 * 
 * - Button: Floating button that opens a menu panel (default, same as mobile)
 * - Row: Horizontal row of menu items rendered in the header
 */
export const BymoPageNavigationMenuDesktopMenuType = {
    /**
     * Floating button that opens a menu panel (default behavior, same as mobile)
     */
    Button: 'Button',
    /**
     * Horizontal row of menu items rendered directly in the header.
     * Items that don't fit will be shown in a "more" dropdown.
     */
    Row: 'Row',
} as const;

export type BymoPageNavigationMenuDesktopMenuType = Values<typeof BymoPageNavigationMenuDesktopMenuType>;

/**
 * Menu panel style types - defines how menu items are displayed and selected state appearance
 * 
 * - Classic: Selected item has 1px underline (default)
 * - Stroke: Selected item shows left arrow indicator 8px from text
 * - BigBold: Selected row filled with main color, text auto-contrasts (black/white)
 * - Centered: Selected item 100% opacity, others 50% opacity, centered text
 * - Buttons: Outlined by default, selected filled, uses main color
 */
export const DoppeViewerNavigationMenuStyle = {
    Classic: 'Classic',
    Stroke: 'Stroke',
    BigBold: 'BigBold',
    Centered: 'Centered',
    Buttons: 'Buttons',
} as const;

export type DoppeViewerNavigationMenuStyle = Values<typeof DoppeViewerNavigationMenuStyle>;


export const DoppeMenuTitleAutoSource = {
    ProfileName: 'profileName',
} as const;

export type DoppeMenuTitleAutoSource = Values<typeof DoppeMenuTitleAutoSource>;

/**
 * The source for a navigation menu title. Can be:
 * - A string for custom title text
 * - An AutoValue (marker) to use the profile name value (whatever it resolves to)
 */
export type DoppeMenuTitleSource = string | AutoValue<DoppeMenuTitleAutoSource>;

/**
 * The source for a navigation menu logo. Can be:
 * - A WixMediaResource for custom media
 * - An AutoValue<'profilePicture'> to use the profile picture from Link in Bio settings
 */
export type DoppeMenuLogoSource = WixMediaResource | AutoValue<'profilePicture'>;

/**
 * Desktop-specific menu settings that can override the default (mobile) settings.
 * When desktopMenuSettings is hidden, desktop uses the same settings as mobile.
 * When visible, these values override the default settings on desktop viewports.
 */
export interface BymoPageNavigationMenuDesktopSettings {
    menuStyle: DoppeViewerNavigationMenuStyle;
    menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType;
    /**
     * Menu type for desktop navigation.
     * - Button: Floating button that opens a menu panel (default, same as mobile)
     * - Row: Horizontal row of menu items in the header, with overflow "more" button
     * @default BymoPageNavigationMenuDesktopMenuType.Button
     */
    menuType: BymoPageNavigationMenuDesktopMenuType;
    /**
     * Maximum number of menu items to show in the row layout.
     * When hidden, all items are shown (no limit).
     * When visible, items beyond this limit will appear in a "more" dropdown.
     * Only applies when menuType is Row.
     * @minimum 3
     */
    maxItemsToShow: HideableValue<number>;
}

export type BymoPageMenuSettings = BymoPageHasSearchProps & {
    /**
     * Visual style for menu items and selected state
     * @default DoppeViewerNavigationMenuStyle.Classic
     */
    menuStyle: DoppeViewerNavigationMenuStyle;
    menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType;
    /**
     * The logo/profile picture displayed in the navigation menu panel. Can be:
     * - Hidden: no logo shown
     * - An AutoValue<DoppePageMediaAutoSource.AccountProfilePicture>: uses the profile picture
     * - A WixMediaResource: custom image
     */
    logo: HideableValue<DoppeMenuLogoSource>;
    /**
     * The title displayed in the navigation menu panel. Can be:
     * - Hidden: no title shown
     * - A string: custom title text
     * - An AutoValue: uses profile name (whatever it resolves to)
     */
    title: HideableValue<DoppeMenuTitleSource>;
    socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance;
    /**
     * Desktop-specific menu settings override.
     * When hidden (default), desktop uses the same settings as mobile.
     * When visible, these settings override the default on desktop viewports.
     */
    desktopMenuSettings: HideableValue<BymoPageNavigationMenuDesktopSettings>;
    /**
     * Whether to show the navigation menu in the header.
     * When false, the header is still displayed but without the menu button/items.
     * @default true
     */
    showMenuInHeader: boolean;
    /**
     * Whether to show a search icon in the navigation header.
     * Clicking the icon opens the full-screen search modal across all pages.
     * @default true
     */
    showSearchIcon: boolean;
    /**
     * Whether to show a cart icon in the navigation header.
     * The icon is only rendered in the viewer if headless eCommerce is configured.
     * @default true
     */
    showCartIcon: boolean;
    /**
     * Whether the home page renders navigation as a header (like inner pages)
     * instead of a floating button on mobile.
     * @default true
     */
    showHeaderMenuInHomePage: boolean;
}

export interface BymoPageNavigationSettings {
    // Navigation always renders as floating button in header
    // layout and position properties removed for simplicity

    // Array of menu items in display order - supports both pages and links
    menuItems: BymoPageNavigationMenuItem[];
    menuSettings: BymoPageMenuSettings;
}

export const BymoPageMenuSettingsContentPropsMetadata = {
    searchPlaceholder: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.NavigationMenuTitleSource, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    showMenuInHeader: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showMenuInHeader'}),
    showSearchIcon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showSearchIcon'}),
    showCartIcon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showCartIcon'}),
    showHeaderMenuInHomePage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showHeaderMenuInHomePage'}),
} satisfies BymoPagePropsContentSettingsMetadata<BymoPageMenuSettings>

export const BymoPageNavigationMenuItemContentPropsMetadata = {
    id: DoppeActionTypeContentSpecialSettingsMetadata.Ignored,
    isVisible: DoppeActionTypeContentSpecialSettingsMetadata.Ignored,
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
} satisfies BymoPagePropsContentSettingsMetadata<BymoPageNavigationMenuItem>

/**
 * Navigation menu item types - discriminated union similar to DoppActionCtaButton
 */
export const BymoPageNavigationMenuItemType = {
    Page: 'Page',
    Link: 'Link',
} as const;
export type BymoPageNavigationMenuItemType = Values<typeof BymoPageNavigationMenuItemType>;

/**
 * Base interface for navigation menu items
 */
export interface BymoPageNavigationMenuItemBase {
    id: string; // Unique identifier for the menu item
    title: string; // Display title in navigation menu
    isVisible: boolean; // Whether this item should be shown in navigation
}

/**
 * Discriminated union for navigation menu item settings
 */
export type BymoPageNavigationMenuItemSettings =
    | BymoPageNavigationMenuItemSettings.Page
    | BymoPageNavigationMenuItemSettings.Link;
export namespace BymoPageNavigationMenuItemSettings {

    type BaseType<T extends BymoPageNavigationMenuItemType> = {
        type: T;
    }

    export type Page = BaseType<typeof BymoPageNavigationMenuItemType.Page> & {
        pageId: string; // ID of the page to navigate to (DoppeDtoInnerPageKnownIds.Home for main page)
        /** Whether to show the page's categories as expandable sub-items in the navigation menu panel. Only applicable for vertical list pages (Products, BlogPosts, Events, Services). */
        showCategoriesInMenu?: boolean;
    };

    export type Link = BaseType<typeof BymoPageNavigationMenuItemType.Link> & {
        url: string; // External URL to navigate to
        openInNewTab?: boolean; // Whether to open link in new tab
    };
}

/**
 * Complete navigation menu item structure
 */
export interface BymoPageNavigationMenuItem extends BymoPageNavigationMenuItemBase {
    settings: BymoPageNavigationMenuItemSettings;
}

export function bymoPageNavigationMenuItemIsHomePageLink (bymoPageNavigationMenuItem: BymoPageNavigationMenuItem): boolean {
    return bymoPageNavigationMenuItem.settings.type === BymoPageNavigationMenuItemType.Page && bymoPageNavigationMenuItem.settings.pageId === DoppeDtoInnerPageKnownIds.Home;
}

/**
 * Input type for migration - requires id and settings.type, all other fields optional
 */
type BymoPageNavigationMenuItemMigrationInput =
    & Pick<BymoPageNavigationMenuItem, 'id'>
    & Partial<Omit<BymoPageNavigationMenuItem, 'id' | 'settings'>>
    & {
        settings:
            | (PartiallyRequired<BymoPageNavigationMenuItemSettings.Page, 'type'>)
            | (PartiallyRequired<BymoPageNavigationMenuItemSettings.Link, 'type'>)
    };

/**
 * Migrates a navigation menu item from partial data with defaults.
 * Ensures backward compatibility when schema is updated - old data gets filled with new defaults.
 * 
 * @param navigationMenuItem - Partial menu item requiring id and settings.type, other fields optional
 * @returns Complete BymoPageNavigationMenuItem with all required fields filled with defaults
 */
export function bymoPageNavigationMenuItemMigrateFromDefaultValues(
    navigationMenuItem: BymoPageNavigationMenuItemMigrationInput
): BymoPageNavigationMenuItem {

    const settings = evaluateFunction((): BymoPageNavigationMenuItemSettings => {
        switch (navigationMenuItem.settings.type) {
            case BymoPageNavigationMenuItemType.Page: {
                const pageSettings = navigationMenuItem.settings as PartiallyRequired<BymoPageNavigationMenuItemSettings.Page, 'type'>;
                return {
                    type: BymoPageNavigationMenuItemType.Page,
                    pageId: pageSettings.pageId ?? DoppeDtoInnerPageKnownIds.Home,
                    showCategoriesInMenu: pageSettings.showCategoriesInMenu ?? false,
                };
            }
            case BymoPageNavigationMenuItemType.Link: {
                const linkSettings = navigationMenuItem.settings as PartiallyRequired<BymoPageNavigationMenuItemSettings.Link, 'type'>;
                return {
                    type: BymoPageNavigationMenuItemType.Link,
                    url: linkSettings.url ?? '',
                    openInNewTab: linkSettings.openInNewTab ?? false,
                };
            }
        }
    });

    return {
        id: navigationMenuItem.id,
        title: navigationMenuItem.title ?? '',
        isVisible: navigationMenuItem.isVisible ?? true,
        settings: settings,
    };

}

/**
 * @deprecated Navigation layout is no longer configurable. Always renders as floating button.
 * Kept for backward compatibility only.
 */
export const BymoPageNavigationLayout = {
    /**
     * @deprecated Tab-style navigation is no longer supported
     */
    Tabs: 'Tabs',
    /**
     * Single floating button that opens a menu (default and only option now)
     */
    Button: 'Button',
} as const;
/**
 * @deprecated Navigation layout is no longer configurable.
 */
export type BymoPageNavigationLayout = Values<typeof BymoPageNavigationLayout>;
/**
 * @deprecated Navigation position is no longer configurable. Always renders in header.
 * Kept for backward compatibility only.
 */
export const BymoPageNavigationPosition = {
    /**
     * Navigation appears in the header area (default and only option now)
     */
    Header: 'Header',
    /**
     * @deprecated Footer navigation is no longer supported
     */
    Footer: 'Footer',
    /**
     * @deprecated InContent navigation is no longer supported
     */
    InContent: 'InContent',
} as const;
/**
 * @deprecated Navigation position is no longer configurable.
 */
export type BymoPageNavigationPosition = Values<typeof BymoPageNavigationPosition>;

/**
 * Resolves the menu title from its source value.
 * 
 * @param titleSource - The title source (string or AutoValue to use profile name)
 * @param resolvedProfileName - The already-resolved profile name string
 * @returns The resolved title string
 */
export function doppeMenuTitleSourceResolve(
    titleSource: DoppeMenuTitleSource,
    resolvedProfileName: string
): string {
    // If it's a string, return it directly (custom title)
    if (typeof titleSource === 'string') {
        return titleSource;
    }

    // If it's an AutoValue, use the profile name
    if (autoValueIsAutoValue(titleSource)) {
        return resolvedProfileName;
    }

    return '';
}

/**
 * Resolves the menu logo from its source value.
 * 
 * @param logoSource - The logo source (WixMediaResource or AutoValue<'profilePicture'>)
 * @param resolvedProfilePicture - The already-resolved profile picture from Link in Bio settings
 * @returns The resolved WixMediaResource or null
 */
export function doppeMenuLogoSourceResolve(
    logoSource: DoppeMenuLogoSource,
    resolvedProfilePicture: WixMediaResource | null
): WixMediaResource | null {
    // If it's an AutoValue, use the profile picture
    if (autoValueIsAutoValue(logoSource)) {
        return resolvedProfilePicture;
    }

    // Otherwise it's a custom WixMediaResource
    return logoSource;
}

export function bymoPageHasNavigationMenuItems(
    bymoPageProps: BymoPageProps
): boolean {
    return bymoPageGetNavigationMenuItems(bymoPageProps).filter(item => !bymoPageNavigationMenuItemIsHomePageLink(item)).length > 0;
}

/**
 * Gets all visible menu items (both pages and links) in the correct order
 * This function can be used by navigation components that support external links
 */
export function bymoPageGetNavigationMenuItems(
    bymoPageProps: BymoPageProps
): BymoPageNavigationMenuItem[] {
    // Get effective menu items (supports both new and legacy structures)
    const menuItems = bymoPageProps.navigationSettings.menuItems;

    // Filter for visible items and validate that page-type items exist
    return menuItems.filter(menuItem => {
        if (!menuItem.isVisible) {
            return false;
        }

        // If it's a page-type menu item, verify the page exists
        if (menuItem.settings.type === BymoPageNavigationMenuItemType.Page) {
            const pageId = menuItem.settings.pageId;

            // Home page always exists
            if (pageId === DoppeDtoInnerPageKnownIds.Home) {
                return true;
            }

            // Check if page exists in any navigatable pages (content, vertical list, or special pages like Cart)
            const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
            return page !== null;
        }

        // Link-type menu items are always included if visible
        return true;
    });
}

export function bymoPageNavigationSettingsIsPageInMenu(bymoPageProps: BymoPageProps, pageId: string): boolean {
    const menuItems = bymoPageProps.navigationSettings.menuItems;
    return menuItems.some((menuItem: BymoPageNavigationMenuItem) => {
        if (menuItem.settings.type === BymoPageNavigationMenuItemType.Page) {
            return menuItem.settings.pageId === pageId;
        }
        return false;
    });
}

export function bymoPageNavigationSettingsIsPageVisibleInMenu(bymoPageProps: BymoPageProps, pageId: string): boolean {
    const menuItems = bymoPageProps.navigationSettings.menuItems;
    const pageMenuItem = menuItems.find((menuItem: BymoPageNavigationMenuItem) => {
        if (menuItem.settings.type === BymoPageNavigationMenuItemType.Page) {
            return menuItem.settings.pageId === pageId;
        }
        return false;
    });
    return pageMenuItem?.isVisible ?? true;
}

export function bymoPageNavigationSettingCanAddPageToMenu (bymoPageProps: BymoPageProps, pageId: string) {
    const canBePotentiallyAdded = evaluateFunction(() => {
        return enumIsEnumValue(DoppeDtoInnerPageKnownIds, pageId) ?
            bymoPageNavigationSettingCanAddSpecialInnerPageToMenu(pageId) :
            bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps) !== null
    })

    return canBePotentiallyAdded ? !bymoPageNavigationSettingsIsPageInMenu(bymoPageProps, pageId) : false;
}

export function bymoPageNavigationSettingCanAddSpecialInnerPageToMenu (pageId: DoppeDtoInnerPageKnownIds) {
    return !enumIsEnumValue(DoppeDtoInnerPageDynamicPagesIds, pageId) && pageId !== DoppeDtoInnerPageKnownIds.Home;
}


/**
 * Checks whether a page menu item points to a vertical list page that supports categories.
 */
export function bymoPageNavigationMenuItemIsVerticalListPage(pageId: string): boolean {
    return enumIsEnumValue(DoppeDtoInnerPageVerticalListPagesIds, pageId);
}

/**
 * Creates a page-type navigation menu item
 */
export function bymoPageCreatePageMenuItem(
    options: {
        title: string;
        pageId: string;
        isVisible?: boolean;
    }
): BymoPageNavigationMenuItem {
    const {title, pageId, isVisible = true} = options;

    return {
        id: uuidCreate(),
        title: title,
        isVisible: isVisible,
        settings: {
            type: BymoPageNavigationMenuItemType.Page,
            pageId: pageId
        }
    };
}

/**
 * Creates a link-type navigation menu item
 */
export function bymoPageCreateLinkMenuItem(options: {
    title: string;
    url: string;
    openInNewTab?: boolean;
    isVisible?: boolean;
}): BymoPageNavigationMenuItem {
    const {title, url, openInNewTab = false, isVisible = true} = options;

    return {
        id: uuidCreate(),
        title: title,
        isVisible: isVisible,
        settings: {
            type: BymoPageNavigationMenuItemType.Link,
            url: url,
            openInNewTab: openInNewTab
        }
    };
}